import { Flex, Stack, VStack, useBreakpointValue, StackDirection } from "@chakra-ui/react";
import { Button} from "@sgi/gravity/Button";
import { FAIcon} from "@sgi/gravity/FAIcon";
import { PhoneInput, PhoneInputProps } from "@sgi/gravity/PhoneInput";
import { Input } from "@sgi/gravity/Input";
import { Typography } from "@sgi/gravity/Typography";
import { Radio, RadioGroup } from "@sgi/gravity/Radio";
import { Link } from "@sgi/gravity/Link";
import { useCallback, useState} from "react";
import { faMessageLines, faPhone } from "@fortawesome/pro-solid-svg-icons";
import Title from "../Common/Title";
import { useMfaSetupProxy } from "../Custom Hooks/useMfaSetupProxy";
import  useFocusControl  from "../Custom Hooks/useFocusControl";


export default function ResetMfa() {
  const [mfaOption, setMfaOption] = useState<string | boolean>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const { continueAction } = useMfaSetupProxy({ mfaOption: mfaOption as 'phone' });
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState('');
  const handlePhoneChange = useCallback<Required<PhoneInputProps>['onChange']>(e => {
    setPhoneNumber(e.value);
    setFormattedPhoneNumber(e.formattedValue);
  }, []);
  
  const phoneNumberError = '';
  const [method, setMethod] = useState("");
  const onChangeMethod = useCallback((value: string | boolean) => setMethod(value.toString()), []);
  const continueButtonDisabled = phoneNumber.length !== 10 || method === '';
  const handleContinueButtonClick = useCallback(() => {
    sessionStorage.setItem('authex-registration-signup-mfa-phone-number', phoneNumber);
    sessionStorage.setItem('authex-registration-signup-mfa-phone-method', method);
    setMfaOption('phone');
    setTimeout(() => continueAction.act(), 100);
  }, [continueAction, phoneNumber, method]);

  const ref = useFocusControl(index => index === 1);
  const direction = useBreakpointValue({ base: "column", md: "row" }) as StackDirection;
  const width = useBreakpointValue({ base: "100%", md: "auto" });

  function handleCancelClick() {
    setPhoneNumber("");
    setFormattedPhoneNumber("");
    window.location.reload();
  }

  return (
    <Flex ref={ref} direction="column" maxW={544} padding={38} mx="auto" justifyContent="center" data-testid="setup-mfa-page-test">
      <Title text="Secure your account" />
      
      <Typography variant="bodyBold">
      For added security, you can set up multi-factor authentication.
      </Typography>
      <Typography variant="body" mb={8}>
        When you enable multi-factor authentication, we'll call or text you a verification code to confirm your account every time you log in.
        &nbsp;<Link href="https://www.mysgi.ca/help_pages/" isExternal={true}>Learn more.</Link>
      </Typography>
      <PhoneInput
        autoFocus
        label="Your Phone Number"
        name="phone"
        id="phone"
        onChange={handlePhoneChange}
        value={formattedPhoneNumber}
        isRequired
        error={phoneNumberError}
        data-testid="phone-number-input-test"
      />
      <Typography variant="bodySmall">
        Verification code will be sent to this number
      </Typography>
      <RadioGroup
        value={method}
        onChange={onChangeMethod}
        name="method"
        label="Verify your phone number by"
        variant="card"
        isRequired
        controlStyleProps={{mt: 4}}
      >
        <Stack direction={direction}>
          <Radio value="text" flex={1} width={width} data-testid="text-me-radio-test">
            <VStack alignItems="center">
              <FAIcon icon={faMessageLines} />
              <span>Text Me</span>
            </VStack>
          </Radio>
          <Radio value="call" flex={1} width={width} data-testid="call-me-radio-test">
            <VStack alignItems="center">
              <FAIcon icon={faPhone} />
              <span>Call Me</span>
            </VStack>
          </Radio>
        </Stack>
      </RadioGroup>
      <Typography variant="body" my={4}>
      Your phone number will be used for account security only. Message and data rates may apply.
      </Typography>
      <Input type="hidden" name="extension_mfaByPhoneOrEmail_phone" ></Input>
      <Input type="hidden" name="extension_mfaByPhoneOrEmail_email" ></Input>
      <Input type="hidden" name="extension_mfaByPhoneOrEmail_none" ></Input>
      <Button
        isFullWidth
        name="continue"
        id="continue"
        data-testid="continue-button-test"
        isDisabled={continueButtonDisabled}
        onClick={handleContinueButtonClick}
        mt={2}
      >
        Continue
      </Button>
      <Button
        isFullWidth
        data-testid="cancel-button-test"
        onClick={handleCancelClick}
        variant="ghost"
        mt={2}
      >
        Cancel
      </Button>
    </Flex>
  );
}